import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BookHero } from '../components/book/BookHero';
import { PackageContents } from '../components/book/PackageContents';
import { BiblePreview } from '../components/book/BiblePreview';

interface BookDetailData {
  bookId: string;
  title: string;
  authorName: string;
  genre: string;
  totalWordCount: number;
  chapterCount: number;
  completedAt: string;
  coverUrl: string | null;
  bible: any | null;
}

export default function BookDetail() {
  const { id } = useParams<{ id: string }>();
  const [book, setBook] = useState<BookDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/book/${id}/detail`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'not_found' : 'failed');
        return res.json();
      })
      .then(setBook)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div role="status" aria-label="Loading book details">
          <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin motion-reduce:hidden" />
          <span className="sr-only">Loading book details</span>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[720px] mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <p className="text-sm text-muted-foreground">Book not found.</p>
          <Link to="/" className="text-xs text-primary mt-4 inline-block">Back to Dashboard</Link>
        </main>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[720px] mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <p className="text-sm text-destructive">Failed to load book details.</p>
          <Link to="/" className="text-xs text-primary mt-4 inline-block">Back to Dashboard</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-[720px] mx-auto px-5 sm:px-8 py-12 sm:py-16 space-y-12">
        <div>
          <div className="text-[10px] tracking-[0.14em] uppercase text-placeholder mb-6">
            <span className="text-primary/60" aria-hidden="true">§</span> Your Book
          </div>

          <BookHero
            bookId={book.bookId}
            title={book.title}
            authorName={book.authorName}
            genre={book.genre}
            totalWordCount={book.totalWordCount}
            chapterCount={book.chapterCount}
            coverUrl={book.coverUrl}
          />
        </div>

        <PackageContents bookId={book.bookId} />

        {book.bible && <BiblePreview bible={book.bible} />}
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="flex items-center justify-between px-5 sm:px-8 py-3.5 border-b border-border">
      <Link to="/" className="font-display text-[17px] text-foreground flex items-center gap-2">
        <span className="w-[7px] h-[7px] rounded-full bg-primary inline-block" aria-hidden="true" />
        Ghostwright
      </Link>
      <Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
        Dashboard
      </Link>
    </header>
  );
}
